import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  IconButton,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { SettingsPopover } from "../SettingsPopover";
import { useTranslation } from "react-i18next";
import { API_DELETE, DBRequest, useConfig } from "../../../../api/api";
import ProjectWorkPackageStack from "../workPackage/ProjectWorkPackageStack";
import { tokens } from "../../../../global/theme/tokens";
import EditIcon from "@mui/icons-material/Edit";
import { useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import FlagIcon from "@mui/icons-material/Flag";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { WorkPackageForm } from "../workPackage/WorkPackageForm";
import { updateOrCreate } from "../../../special/updateOrCreate";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonIcon from "@mui/icons-material/Person";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export const ProjectPhase = ({
  phase,
  onDelete,
  nr,
  onPhaseChange,
  onEdit,
  editmode,
  project,
  isSorting,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();
  const config = useConfig();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.id, disabled: !editmode });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : "auto",
    opacity: isDragging ? 0.4 : 1,
  };

  const [isIn, setIsIn] = useState(true);

  const [showOwnWP, setShowOwnWP] = useState([]);

  const [openWPForm, setOpenWPForm] = useState(false);
  const [editWP, setEditWP] = useState(null);

  function handleWorkPackageChange(updatedWP) {
    onPhaseChange({
      ...phase,
      workPackages: updateOrCreate(phase.workPackages, updatedWP, true),
    });
  }

  // hide wps with a certaint status
  const [allowedStatuses, setAllowedStatuses] = useState([0, 2]);

  const settings = [
    {
      label: t("Edit"),
      Icon: EditIcon,
      onClick: () => onEdit(phase),
    },
    {
      label: t("Delete"),
      Icon: DeleteIcon,
      onClick: handleDelete,
      color: "error",
    },
  ];

  function handleDelete() {
    DBRequest({
      config,
      path: `project-phases/${phase.id}`,
      method: API_DELETE(),
      onResponse: () => onDelete(phase),
    });
  }

  return (
    <Box
      ref={setNodeRef}
      style={sortableStyle}
      className=" backdrop-blur-lg rounded-lg"
      sx={{
        ".actions": {
          opacity: 0,
          transition: "0.5s ease",
        },
        "button:hover .actions": {
          opacity: 1,
        },
        "&:hover .drag-handle": {
          color: colors.info,
          opacity: 1,
        },
        " .add-wp": {
          opacity: 0,
          transition: "0.5s ease",
          transform: "scale(0)",
        },
        ":hover .add-wp": {
          opacity: 1,
          transform: "scale(1)",
        },
        "@media (max-width: 700px)": {
          ".add-wp": {
            opacity: 1,
            transform: "scale(1)",
          },
        },
      }}
    >
      <Button
        variant="contained"
        onClick={() => setIsIn(!isIn)}
        className="flex items-center w-full justify-between mb-4 px-4 py-2 rounded-lg"
        sx={{
          justifyContent: "space-between",
          borderRadius: "0.5rem",
          bgcolor: colors.glass,
          ":hover": {
            bgcolor: colors.selected,
          },
        }}
        disableElevation
      >
        <Box className="flex items-center gap-3">
          {editmode && (
            <Box
              {...attributes}
              {...listeners}
              className="drag-handle cursor-grab flex items-center"
              onClick={(e) => e.stopPropagation()}
              sx={{
                color: colors.grey[500],
                transition: "color 0.15s, opacity 0.15s",
                opacity: 0.5,
                "&:hover": { color: colors.info, opacity: 1 },
              }}
            >
              <DragIndicatorIcon sx={{ fontSize: "20px" }} />
            </Box>
          )}
          {!editmode && (
            <KeyboardArrowDownIcon
              sx={{
                transform: `rotate(${isIn ? "180deg" : "0deg"})`,
                transition: "0.4s",
              }}
            />
          )}
          <Typography fontSize={16} fontWeight={600}>
            {phase.name}
          </Typography>
        </Box>

        {editmode && (
          <div className="actions" onClick={(e) => e.stopPropagation()}>
            <SettingsPopover settings={settings} />
          </div>
        )}
      </Button>
      <Collapse in={isIn && !isSorting}>
        <Box className="flex flex-row gap-4 pl-4 pt-4">
          <ToggleButtonGroup
            size="small"
            value={allowedStatuses}
            onChange={(e, value) => setAllowedStatuses(value)}
            aria-label="WP Status"
            sx={{
              backdropFilter: "blur(10px)",
              borderRadius: 1,
            }}
          >
            <Tooltip title={t("Running")}>
              <ToggleButton value={0}>
                <DirectionsRunIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("Infinite")}>
              <ToggleButton value={2}>
                <AllInclusiveIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("Finished")}>
              <ToggleButton value={1}>
                <FlagIcon />
              </ToggleButton>
            </Tooltip>
            <Tooltip title={t("Canceled")}>
              <ToggleButton value={3}>
                <ReportGmailerrorredIcon />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            radioGroup
            size="small"
            value={showOwnWP}
            onChange={(e, value) => setShowOwnWP(value)}
            aria-label="WP Status"
            sx={{
              backdropFilter: "blur(10px)",
              borderRadius: 1,
            }}
          >
            <Tooltip title={t("Only own Workpackages")}>
              <ToggleButton value={true}>
                <PersonIcon />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>

        <ProjectWorkPackageStack
          project={project}
          phase={phase}
          onPhaseChange={onPhaseChange}
          editmode={editmode}
          allowedStatuses={allowedStatuses}
          showOwnWP={showOwnWP.length > 0}
          onEditWP={setEditWP}
          openWPForm={openWPForm}
          setOpenWPForm={setOpenWPForm}
          handleWorkPackageChange={handleWorkPackageChange}
        />
      </Collapse>
      <WorkPackageForm
        phaseId={phase.id}
        open={openWPForm}
        onClose={() => {
          setOpenWPForm(false);
          setEditWP(null);
        }}
        onChange={handleWorkPackageChange}
        startDataCollection={editWP}
        projectId={project?.id}
      />
    </Box>
  );
};
